import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";

/**
 * Statistics Section – investor-oriented proof-points
 * – Focus on hard ROI (payback, fuel savings) + climate metrics.
 * – Count-up animation supports integers & decimals.
 */
const Statistics = () => {
  const [isVisible, setIsVisible] = useState(false);

  /** Core headline metrics (grid) */
  const stats = [
    {
      value: 99,
      suffix: "%",
      label: "NOx Emission Cut",
      description: "Compared with legacy turbines",
    },
    {
      value: 2.5,
      suffix: " M$",
      decimals: 1,
      label: "Annual Fuel Savings*",
      description: "Per 50 MW installation",
    },
    {
      value: 18,
      suffix: " mos",
      label: "Typical Payback",
      description: "Achieved on CHP projects",
    },
    {
      value: 650,
      suffix: " MW",
      label: "Green Capacity Online",
      description: "Hydrogen-ready fleet worldwide",
    },
  ];

  /** Helper: count-up animation with decimal support */
  const CountUp = ({ end, duration = 2, decimals = 0 }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (!isVisible) return;
      let start; // DOMHighResTimeStamp
      const step = (t) => {
        if (!start) start = t;
        const progress = Math.min((t - start) / (duration * 1000), 1);
        const val = end * progress;
        setCount(decimals ? val.toFixed(decimals) : Math.floor(val));
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, [end, duration, decimals, isVisible]);

    return count;
  };

  return (
    <section className="py-20 bg-secondary-900 text-white relative overflow-hidden">
      {/* Decorative radials */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, #22c55e 0%, transparent 50%), radial-gradient(circle at 80% 70%, #16a34a 0%, transparent 50%)",
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          onViewportEnter={() => setIsVisible(true)}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Impact That Investors Notice
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            GreenGasTurbines delivers bankable returns and verified climate
            benefits—fuel savings, rapid payback and near-zero emissions in one
            package.
          </p>
        </motion.div>

        {/* Metrics grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map(({ value, suffix, label, description, decimals = 0 }, i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.15 }}
              className="text-center group"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-primary-500/50 transition-all duration-300"
              >
                <div className="text-5xl md:text-6xl font-bold text-primary-400 mb-2">
                  <CountUp end={value} decimals={decimals} />
                  <span className="text-primary-300">{suffix}</span>
                </div>
                <h3 className="text-xl font-semibold mb-2 text-white">
                  {label}
                </h3>
                <p className="text-gray-400 text-sm">{description}</p>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Environmental impact trio */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-20 text-center"
        >
          <div className="bg-gradient-to-r from-primary-600/20 to-primary-500/20 rounded-2xl p-8 md:p-12 border border-primary-500/20">
            <h3 className="text-3xl font-bold mb-6 text-white">
              Climate &amp; Resource Savings
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { value: "2.8M", label: "t CO₂e Avoided / yr" },
                { value: "18B", label: "Litres Water Saved" },
                { value: "97%", label: "Fleet-wide Customer Loyalty" },
              ].map(({ value, label }) => (
                <div key={label}>
                  <div className="text-4xl font-bold text-primary-400 mb-2">{value}</div>
                  <div className="text-gray-300">{label}</div>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-6">
              *Fuel-savings estimate based on a 50 MW combined heat &amp; power
              plant at 7,000 FLH, US Gulf benchmark gas price.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Statistics;